import { create } from 'zustand';
import type {
  Route,
  RouteProfile,
  Coordinates,
  NavigationState,
  RouteStep,
} from '@/types';

export type RouteType = 'fastest' | 'shortest' | 'scenic' | 'eco';

interface Waypoint {
  id: string;
  coordinates: Coordinates | null;
  address: string;
  type: 'origin' | 'destination' | 'waypoint';
}

interface RoutingState {
  // Waypoints
  waypoints: Waypoint[];
  activeWaypointId: string | null;

  // Route profile
  profile: RouteProfile;
  routeType: RouteType;
  avoidTolls: boolean;
  avoidHighways: boolean;
  avoidFerries: boolean;
  avoidUnpaved: boolean;

  // Departure time
  departureTime: Date | null;
  trafficModel: 'best_guess' | 'pessimistic' | 'optimistic';

  // Routes
  routes: Route[];
  selectedRouteIndex: number;
  isCalculating: boolean;
  routeError: string | null;

  // Live location sharing
  isSharing: boolean;
  shareCode: string | null;

  // Navigation
  navigation: NavigationState;

  // Actions
  setWaypoint: (id: string, coordinates: Coordinates | null, address: string) => void;
  addWaypoint: () => void;
  removeWaypoint: (id: string) => void;
  reorderWaypoints: (fromIndex: number, toIndex: number) => void;
  applyOptimizedOrder: (order: number[]) => boolean;
  swapOriginDestination: () => void;
  setActiveWaypointId: (id: string | null) => void;
  setProfile: (profile: RouteProfile) => void;
  setRouteType: (routeType: RouteType) => void;
  setAvoidOption: (option: 'tolls' | 'highways' | 'ferries' | 'unpaved', value: boolean) => void;
  setDepartureTime: (time: Date | null) => void;
  setTrafficModel: (model: 'best_guess' | 'pessimistic' | 'optimistic') => void;
  setRoutes: (routes: Route[]) => void;
  setSelectedRouteIndex: (index: number) => void;
  setIsCalculating: (calculating: boolean) => void;
  setRouteError: (error: string | null) => void;
  setSharing: (isSharing: boolean, shareCode: string | null) => void;
  startNavigation: () => void;
  stopNavigation: () => void;
  updateNavigationPosition: (position: Coordinates, stats?: NavigationProgress) => void;
  setNavigationStep: (stepIndex: number) => void;
  setRerouting: (isRerouting: boolean) => void;
  replaceNavigationRoute: (route: Route) => void;
  toggleVoice: () => void;
  clearRoute: () => void;
  reset: () => void;
}

// Live trip stats recomputed each GPS fix by NavigationMode (which owns the
// route-geometry projection). The store stays the single source of truth for the
// values the UI renders.
interface NavigationProgress {
  distanceRemaining: number;
  durationRemaining: number;
  arrivalTime: Date | null;
}

const createInitialWaypoints = (): Waypoint[] => [
  { id: 'origin', coordinates: null, address: '', type: 'origin' },
  { id: 'destination', coordinates: null, address: '', type: 'destination' },
];

const initialNavigationState: NavigationState = {
  isActive: false,
  route: null,
  currentStepIndex: 0,
  currentPosition: null,
  distanceRemaining: 0,
  durationRemaining: 0,
  arrivalTime: null,
  isRerouting: false,
  voiceEnabled: true,
};

export const useRoutingStore = create<RoutingState>((set, get) => ({
  // Initial state
  waypoints: createInitialWaypoints(),
  activeWaypointId: null,
  profile: 'driving',
  routeType: 'fastest',
  avoidTolls: false,
  avoidHighways: false,
  avoidFerries: false,
  avoidUnpaved: false,
  departureTime: null,
  trafficModel: 'best_guess',
  routes: [],
  selectedRouteIndex: 0,
  isCalculating: false,
  routeError: null,
  isSharing: false,
  shareCode: null,
  navigation: initialNavigationState,

  // Actions
  setWaypoint: (id, coordinates, address) => set((state) => ({
    waypoints: state.waypoints.map((wp) =>
      wp.id === id ? { ...wp, coordinates, address } : wp
    ),
  })),

  addWaypoint: () => set((state) => {
    const newWaypoint: Waypoint = {
      // Date.now() alone collides when two stops are added within the same ms
      // (duplicate React keys + colliding marker ids); add a random suffix.
      id: `waypoint-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      coordinates: null,
      address: '',
      type: 'waypoint',
    };
    const destIndex = state.waypoints.findIndex((wp) => wp.type === 'destination');
    const newWaypoints = [...state.waypoints];
    newWaypoints.splice(destIndex, 0, newWaypoint);
    return { waypoints: newWaypoints };
  }),

  removeWaypoint: (id) => set((state) => {
    const wp = state.waypoints.find((w) => w.id === id);
    if (wp?.type === 'origin' || wp?.type === 'destination') {
      return {
        waypoints: state.waypoints.map((w) =>
          w.id === id ? { ...w, coordinates: null, address: '' } : w
        ),
      };
    }
    return {
      waypoints: state.waypoints.filter((w) => w.id !== id),
    };
  }),

  reorderWaypoints: (fromIndex, toIndex) => set((state) => {
    const newWaypoints = [...state.waypoints];
    const [removed] = newWaypoints.splice(fromIndex, 1);
    newWaypoints.splice(toIndex, 0, removed);
    return { waypoints: newWaypoints };
  }),

  // applyOptimizedOrder reorders the waypoints by a permutation of their current
  // indices (the optimized_order from the routing service). Returns whether it was
  // applied. Rejects anything that isn't a valid permutation, OR that would move
  // the origin off index 0 or the destination off the last index — type lives on
  // the waypoint object, not its position, so a bad order would break the
  // origin/destination invariant the rest of the UI relies on.
  applyOptimizedOrder: (order) => {
    let applied = false;
    set((state) => {
      const n = state.waypoints.length;
      if (order.length !== n) return state;
      const sorted = [...order].sort((a, b) => a - b);
      if (sorted.some((v, i) => v !== i)) return state; // not a permutation
      if (order[0] !== 0 || order[n - 1] !== n - 1) return state; // endpoints not pinned
      applied = true;
      return { waypoints: order.map((i) => state.waypoints[i]) };
    });
    return applied;
  },

  swapOriginDestination: () => set((state) => {
    const origin = state.waypoints.find((wp) => wp.type === 'origin');
    const destination = state.waypoints.find((wp) => wp.type === 'destination');
    if (!origin || !destination) return state;

    return {
      waypoints: state.waypoints.map((wp) => {
        if (wp.type === 'origin') {
          return {
            ...wp,
            coordinates: destination.coordinates,
            address: destination.address,
          };
        }
        if (wp.type === 'destination') {
          return {
            ...wp,
            coordinates: origin.coordinates,
            address: origin.address,
          };
        }
        return wp;
      }),
    };
  }),

  setActiveWaypointId: (id) => set({ activeWaypointId: id }),

  setProfile: (profile) => set({ profile }),

  setRouteType: (routeType) => set({ routeType }),

  setAvoidOption: (option, value) => set((state) => {
    switch (option) {
      case 'tolls':
        return { avoidTolls: value };
      case 'highways':
        return { avoidHighways: value };
      case 'ferries':
        return { avoidFerries: value };
      case 'unpaved':
        return { avoidUnpaved: value };
      default:
        return state;
    }
  }),

  setDepartureTime: (time) => set({ departureTime: time }),

  setTrafficModel: (model) => set({ trafficModel: model }),

  setSharing: (isSharing, shareCode) => set({ isSharing, shareCode }),

  setRoutes: (routes) => set({ routes, selectedRouteIndex: 0 }),

  setSelectedRouteIndex: (index) => set({ selectedRouteIndex: index }),

  setIsCalculating: (calculating) => set({ isCalculating: calculating }),

  setRouteError: (error) => set({ routeError: error }),

  startNavigation: () => {
    const { routes, selectedRouteIndex } = get();
    const route = routes[selectedRouteIndex];
    if (!route) return;

    set({
      navigation: {
        isActive: true,
        route,
        currentStepIndex: 0,
        currentPosition: route.legs[0]?.startLocation ?? null,
        distanceRemaining: route.distance,
        durationRemaining: route.durationInTraffic ?? route.duration,
        arrivalTime: new Date(Date.now() + (route.durationInTraffic ?? route.duration) * 1000),
        isRerouting: false,
        voiceEnabled: true,
      },
    });
  },

  stopNavigation: () => set({
    navigation: initialNavigationState,
  }),

  // The trip stats (distance/duration/arrival) are recomputed by NavigationMode
  // from the user's projection onto the real route polyline and passed in here,
  // so the displayed numbers count down as the trip progresses.
  updateNavigationPosition: (position, stats) => set((state) => {
    if (!state.navigation.isActive || !state.navigation.route) {
      return state;
    }

    return {
      navigation: {
        ...state.navigation,
        currentPosition: position,
        ...(stats
          ? {
              distanceRemaining: stats.distanceRemaining,
              durationRemaining: stats.durationRemaining,
              arrivalTime: stats.arrivalTime,
            }
          : {}),
      },
    };
  }),

  setRerouting: (isRerouting) => set((state) => ({
    navigation: { ...state.navigation, isRerouting },
  })),

  // Swap in a freshly calculated route (after an off-route reroute), resetting
  // progress to the new route's full length and the maneuver cursor to the start.
  replaceNavigationRoute: (route) => set((state) => {
    if (!state.navigation.isActive) return state;
    const duration = route.durationInTraffic ?? route.duration;
    return {
      navigation: {
        ...state.navigation,
        route,
        currentStepIndex: 0,
        distanceRemaining: route.distance,
        durationRemaining: duration,
        arrivalTime: new Date(Date.now() + duration * 1000),
        isRerouting: false,
      },
    };
  }),

  setNavigationStep: (stepIndex) => set((state) => ({
    navigation: {
      ...state.navigation,
      currentStepIndex: stepIndex,
    },
  })),

  toggleVoice: () => set((state) => ({
    navigation: {
      ...state.navigation,
      voiceEnabled: !state.navigation.voiceEnabled,
    },
  })),

  clearRoute: () => set({
    routes: [],
    selectedRouteIndex: 0,
    routeError: null,
  }),

  reset: () => set({
    waypoints: createInitialWaypoints(),
    activeWaypointId: null,
    profile: 'driving',
    routeType: 'fastest',
    avoidTolls: false,
    avoidHighways: false,
    avoidFerries: false,
    avoidUnpaved: false,
    departureTime: null,
    trafficModel: 'best_guess',
    routes: [],
    selectedRouteIndex: 0,
    isCalculating: false,
    routeError: null,
    isSharing: false,
    shareCode: null,
    navigation: initialNavigationState,
  }),
}));

// Helper function for navigation - used in production for step-by-step directions
function _getCurrentStep(route: Route, stepIndex: number): RouteStep | null {
  let currentIndex = 0;
  for (const leg of route.legs) {
    for (const step of leg.steps) {
      if (currentIndex === stepIndex) {
        return step;
      }
      currentIndex++;
    }
  }
  return null;
}

// Export for use in navigation components
export { _getCurrentStep as getCurrentStep };
